import { DisplayAreaContentsInfo } from '@wconcept/wconcept-common/models/display/display';
import { BrandInfo } from '@wconcept/wconcept-common/models/renewal/brand';
import {
  DisplayContentInfo,
  ProfileInfo,
} from '@wconcept/wconcept-common/models/renewal/display';
import { MyHeartBrandInfo } from '@wconcept/wconcept-common/models/renewal/myHeart';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import {
  getCookieAdfrom,
  getCookieChannelId,
} from '@wconcept/wconcept-common/utils/cookie';
import { Brand } from '@components/pages/shopPilot/types';
import { useCallback, useRef } from 'react';
import { isExcludedAdfrom } from '@components/common/AdFromComponent';
import { requestSmartBannerData } from '@apis/renewal/smartBanner';
import { CACHE_KEY } from '@constants/cacheKey';
import useGlobalState from './useGlobalState';
import { useWckQuery } from './wckQuery/useWckQuery';

const BannerStyle = {
  '#airbridge-banner': {
    padding: '10px',
    backgroundColor: '#f6f6f6',
  },
  '#airbridge-banner *': {
    fontFamily: "'Pretendard', sans-serif !important",
  },
  '#airbridge-banner-close': {
    width: '16px',
    height: '16px',
    background:
      'url("//image.wconcept.co.kr/images/img/smartBanner/IconClose.svg") no-repeat center / contain',
    margin: '0',
  },
  '#airbridge-banner-close svg': {
    display: 'none',
  },
  '#airbridge-banner-icon': {
    marginLeft: '10px',
    width: '32px',
    height: '32px',
    borderRadius: '10.667px',
  },
  '#airbridge-banner-icon + section': {
    marginLeft: '10px !important',
    rowGap: '1px !important',
  },
  '#airbridge-banner-title': {
    fontWeight: '700',
    fontSize: '13px',
    lineHeight: '130%',
    color: '#000',
  },
  '#airbridge-banner-description': {
    fontWeight: '400',
    fontSize: '11px',
    lineHeight: '130%',
    color: '#777',
  },
  '#airbridge-banner-open': {
    fontWeight: '600',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    margin: '0 0 0 13px',
    padding: '6.75px 7px',
    border: '1px solid #e2e2e2',
    borderRadius: '0',
    background: '#fff',
    color: '#000',
  },
};

// openBanner destination 객체 타입 (SDK에 참조로 전달되어 in-place 갱신용으로 사용)
interface AirBridgeBannerDestination {
  type: 'deeplink';
  deeplinks: {
    android: string;
    ios: string;
    desktop: string;
  };
  fallbacks: {
    android: string;
    ios: string;
  };
  ctaParams?: {
    cta_param_1?: string;
    cta_param_2?: string;
  };
}

function useAirBridgeEvent() {
  const { pageInfo, appInfo, isMobile, domainGender, assistData } =
    useGlobalState();
  // 중복호출 방지용
  const hasOpened = useRef(false);
  // openBanner에 전달한 destination 객체 참조를 보관.
  // SDK가 클릭 시점에 이 객체의 deeplinks 필드를 그대로 읽으므로,
  // 라우트 변경 시 이 객체의 deeplinks를 in-place로 갱신하면 deeplink가 최신 URL로 동작한다.
  const destinationRef = useRef<AirBridgeBannerDestination | null>(null);

  const { data: bannerData } = useWckQuery({
    queryKey: [CACHE_KEY.SMART_BANNER.SMART_BANNER_DATA],
    queryFn: requestSmartBannerData,
    staleTime: 1000 * 60 * 3,
    gcTime: 1000 * 60 * 3,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: isMobile,
  });

  interface AirBridgeProductParams {
    productID?: string;
    name?: string;
    price?: number;
    categoryName?: string;
    brandID?: string | number;
    brandName?: string;
    quantity?: number;
    currency?: string;
  }
  interface AirBridgeSemanticEventParams {
    level?: string;
    place?: string;
    currency?: string;
    products: AirBridgeProductParams[];
  }
  interface AirBridgeWproductParams {
    wProductID?: string;
    Original_price?: number;
    product_discount_amount?: number;
    coupon_discount_amount?: number;
    display_pirce?: number;
    total_discount_rate?: string;
  }
  interface AirBridgeCustomEventParams {
    content_id?: number;
    content_name?: string;
    creator_id?: string;
    wProducts?: AirBridgeWproductParams[];
    level_name?: string;
  }

  const getProfileType = useCallback((info?: ProfileInfo) => {
    if (info?.profileType === 'SELLER') return 'Brand';
    if (info?.profileType === 'WCONCEPT') return 'W컨셉';
    // if (info?.profileType === 'CUSTOMER') return '';
    if (info?.profile.creatorStatusCd === 'Y') return 'W크리에이터';
    if (info?.profile.conceptorsYn === 'Y') return '컨셉터즈';
    return '';
  }, []);

  const sendAirBridgeEvent = useCallback(
    ({
      eventName,
      action,
      label,
      value,
      semanticEventValue,
      custonEventValue,
    }: {
      eventName: string;
      action?: string;
      label?: string | number;
      value?: number;
      currency?: string;
      semanticEventValue?: AirBridgeSemanticEventParams;
      custonEventValue?: AirBridgeCustomEventParams;
    }) => {
      if (window?.airbridge && window?.airbridge?.isSDKEnabled) {
        window.airbridge.setUserID(assistData?.user?.userNo || '0');
        window.airbridge.events.send(eventName, {
          action,
          label,
          value,
          semanticAttributes: semanticEventValue,
          customAttributes: custonEventValue,
        });
      }
    },
    [assistData?.user?.userNo]
  );

  const sendAirBridgeMyHeartBrand = useCallback(
    async ({
      value,
      heratYn,
    }: {
      value?:
        | DisplayAreaContentsInfo
        | BrandInfo
        | DisplayContentInfo
        | MyHeartBrandInfo
        | Brand;
      heratYn?: boolean;
    }) => {
      // 마이하트 등록한 시점에만
      if (!heratYn) return;
      const channelId = await getCookieChannelId();

      sendAirBridgeEvent({
        eventName: 'my_heart_brand_clicked',
        action: value?.brandNameKr || value?.brandName,
        label: channelId,
        semanticEventValue: {
          level: assistData?.user?.grade,
          products: [
            {
              brandID: value?.brandCd || 0,
              brandName: value?.brandNameKr || value?.brandName,
            },
          ],
        },
        custonEventValue: {
          level_name: assistData?.user?.gradeName,
        },
      });
    },
    [assistData, sendAirBridgeEvent]
  );

  const sendAirBridgeMyHeartProduct = useCallback(
    async ({ product, heratYn }: { product?: Product; heratYn?: boolean }) => {
      const channelId = await getCookieChannelId();
      const categoryPath = [
        product?.mediumName,
        product?.categoryDepthName1,
        product?.categoryDepthName2,
        product?.categoryDepthName3,
      ]
        .filter(Boolean)
        .join('>');
      // 마이하트 등록한 시점에만
      if (!heratYn) return;
      sendAirBridgeEvent({
        eventName: 'airbridge.addToWishlist',
        action: pageInfo?.page || pageInfo?.pageTitle,
        label: channelId,
        value: product?.salePrice,
        semanticEventValue: {
          level: assistData?.user?.grade,
          currency: 'KRW',
          products: [
            {
              productID: product?.itemCd,
              name: product?.itemName,
              price: product?.salePrice,
              categoryName: categoryPath,
              brandID: product?.brandCd,
              brandName: product?.brandNameKr,
              currency: 'KRW',
            },
          ],
        },
        custonEventValue: {
          level_name: assistData?.user?.gradeName,
          wProducts: [
            {
              wProductID: product?.itemCd,
              Original_price: product?.customerPrice,
              product_discount_amount: 0,
              coupon_discount_amount: 0,
              display_pirce: product?.finalPrice,
              total_discount_rate: `${product?.finalDiscountRate}%`,
            },
          ],
        },
      });
    },
    [assistData, pageInfo, sendAirBridgeEvent]
  );

  const sendAirBridgeMyHeartShortForm = useCallback(
    async ({
      contentId,
      contentName,
      creatorId,
      profile,
      productList,
      heratYn,
    }: {
      contentId?: number;
      contentName?: string;
      creatorId?: string;
      profile?: ProfileInfo;
      productList?: Product[];
      heratYn?: boolean;
    }) => {
      const channelId = await getCookieChannelId();
      // 마이하트 등록한 시점에만
      if (!heratYn) return;
      sendAirBridgeEvent({
        eventName: 'sf_my_heart_clicked',
        action: getProfileType(profile),
        label: channelId,
        semanticEventValue: {
          level: assistData?.user?.grade,
          place: pageInfo?.page || pageInfo?.pageTitle,
          products:
            productList?.map(item => ({ productID: item.itemCd })) || [],
        },
        custonEventValue: {
          level_name: assistData?.user?.gradeName,
          content_id: contentId,
          content_name: contentName,
          creator_id: creatorId,
        },
      });
    },
    [assistData, getProfileType, pageInfo, sendAirBridgeEvent]
  );
  const sendAirBridgeShareShortForm = useCallback(
    async ({
      contentId,
      contentName,
      creatorId,
      profile,
      productList,
    }: {
      contentId?: number;
      contentName?: string;
      creatorId?: string;
      profile?: ProfileInfo;
      productList?: Product[];
    }) => {
      const channelId = await getCookieChannelId();
      sendAirBridgeEvent({
        eventName: 'sf_share_clicked',
        action: getProfileType(profile),
        label: channelId,
        semanticEventValue: {
          level: assistData?.user?.grade,
          products:
            productList?.map(item => ({ productID: item.itemCd })) || [],
        },
        custonEventValue: {
          level_name: assistData?.user?.gradeName,
          content_id: contentId,
          content_name: contentName,
          creator_id: creatorId,
        },
      });
    },
    [assistData, getProfileType, sendAirBridgeEvent]
  );

  const openAirBridgeAppBanner = useCallback(async () => {
    // URL체크 (모바일 대응)
    const params = new URLSearchParams(window.location.search);
    const adFromUrl = params.get('adfrom');
    const adFrom = adFromUrl ?? (await getCookieAdfrom());
    const isAdFrom = isExcludedAdfrom(adFrom);
    const url = new URL(window.location.href);
    const pageUrl = url.host + url.pathname + (url.search ? url.search : '');
    const newDeeplink = `wconceptkr://${pageUrl}`;
    if (
      window.airbridge &&
      isMobile &&
      !appInfo.isNativeAppBoth &&
      (isAdFrom || !adFrom) &&
      bannerData
    ) {
      if (!hasOpened.current) {
        // 첫 호출: destination 객체를 ref로 보관하여 SDK 클로저에 동일 참조를 전달.
        // 이후 라우트 변경 시 이 객체의 deeplinks 필드를 직접 갱신하면 SDK가 새 URL로 동작한다.
        destinationRef.current = {
          type: 'deeplink',
          deeplinks: {
            android: newDeeplink,
            ios: newDeeplink,
            desktop: '',
          },
          fallbacks: {
            android: 'google-play',
            ios: 'itunes-appstore',
          },
          ctaParams: {
            cta_param_1: pageInfo?.page,
            cta_param_2: 'smart_banner',
          },
        };
        window.airbridge.openBanner({
          title: assistData.isLogin
            ? bannerData?.loginTitle ?? '최대 10% APP 전용 쿠폰'
            : bannerData?.nonLoginTitle ?? '최대 30% 쿠폰팩',
          description: assistData.isLogin
            ? bannerData?.loginSubTitle ?? 'APP에서 즉시 확인'
            : bannerData?.nonLoginSubTitle ?? '가입시 즉시 지급',
          buttonText: bannerData?.buttonText ?? '앱으로 보기',
          color: 'black',
          position: 'top',
          destination: destinationRef.current,
          styles: BannerStyle,
        });
        hasOpened.current = true;
      } else if (destinationRef.current) {
        // 재호출(라우트 변경 등): openBanner를 다시 호출할 수 없으므로
        // 보관 중인 destination 객체의 deeplinks를 in-place로 갱신한다.
        destinationRef.current.deeplinks.android = newDeeplink;
        destinationRef.current.deeplinks.ios = newDeeplink;
        if (destinationRef.current.ctaParams) {
          destinationRef.current.ctaParams.cta_param_1 = pageInfo?.page;
        }
      }
    }
  }, [isMobile, assistData, appInfo, pageInfo, bannerData]);

  return {
    sendAirBridgeEvent,
    sendAirBridgeMyHeartBrand,
    sendAirBridgeMyHeartProduct,
    sendAirBridgeMyHeartShortForm,
    sendAirBridgeShareShortForm,
    openAirBridgeAppBanner,
  };
}

export default useAirBridgeEvent;
